import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import {
  calculateGracePeriodDays,
  createPortalSession
} from '../../services/subscriptionService'

export interface PaymentDueBannerProps {
  officeId: string
  gracePeriodEnd: Date
  isOwner: boolean
  ownerEmail?: string
}

/**
 * PaymentDueBanner Component
 *
 * Displays a prominent warning banner when an office is in grace period due to payment failure.
 * The banner shows:
 * - Days remaining in grace period
 * - For owners: "Update Payment Method" button linking to Stripe portal
 * - For non-owners: Owner contact information
 * - Auto-hides when grace period ends or payment succeeds
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
 *
 * @param officeId - The office ID
 * @param gracePeriodEnd - The date when the grace period expires
 * @param isOwner - Whether the current user is the office owner
 * @param ownerEmail - The office owner's email (for non-owners)
 */
export const PaymentDueBanner = ({
  officeId,
  gracePeriodEnd,
  isOwner,
  ownerEmail
}: PaymentDueBannerProps) => {
  const [daysRemaining, setDaysRemaining] = useState<number>(0)
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    // Calculate days remaining
    const days = calculateGracePeriodDays(gracePeriodEnd)
    setDaysRemaining(days)

    // Auto-hide if grace period has ended
    if (days <= 0) {
      setIsVisible(false)
    }

    // Set up interval to update days remaining every minute
    const interval = setInterval(() => {
      const updatedDays = calculateGracePeriodDays(gracePeriodEnd)
      setDaysRemaining(updatedDays)

      // Auto-hide if grace period has ended
      if (updatedDays <= 0) {
        setIsVisible(false)
        clearInterval(interval)
      }
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [gracePeriodEnd])

  const handleUpdatePayment = async () => {
    try {
      setIsLoadingPortal(true)
      const portalUrl = await createPortalSession(officeId)
      // Redirect to Stripe Customer Portal
      window.location.href = portalUrl
    } catch (err) {
      console.error('Error opening billing portal:', err)
      setIsLoadingPortal(false)
    }
  }

  // Don't render if not visible
  if (!isVisible) {
    return null
  }

  return (
    <Alert
      severity='error'
      icon={<ErrorOutlineIcon fontSize='large' />}
      sx={{
        mb: 2,
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
      data-testid='payment-due-banner'
    >
      <AlertTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
        Payment Failed - Action Required
      </AlertTitle>

      <Box sx={{ mt: 1 }}>
        <Typography variant='body1' sx={{ mb: 1 }}>
          Your subscription payment has failed. You have{' '}
          <strong data-testid='days-remaining'>
            {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
          </strong>{' '}
          remaining to update your payment method before your office is
          downgraded to the Free tier.
        </Typography>

        {isOwner ? (
          <Box sx={{ mt: 2 }}>
            <Button
              variant='contained'
              color='error'
              onClick={handleUpdatePayment}
              disabled={isLoadingPortal}
              data-testid='update-payment-button'
            >
              {isLoadingPortal ? 'Loading...' : 'Update Payment Method'}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: 1
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              You are not the office owner. Please contact the office owner to
              resolve this issue:
            </Typography>
            {ownerEmail && (
              <Typography
                variant='body2'
                sx={{ mt: 0.5, fontWeight: 600 }}
                data-testid='owner-email'
              >
                {ownerEmail}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Alert>
  )
}

export default PaymentDueBanner
